/**
 * Custom GSAP Full-Screen Drawer for Dawn Theme
 * File: assets/custom-gsap-drawer.js
 * 
 * This script handles all the GSAP animations and interactions
 * for the custom full-screen drawer implementation.
 */

(function() {
  'use strict';

  // Configuration
  const CONFIG = {
    // Animation durations (in seconds)
    OPEN_DURATION: 0.8,
    CLOSE_DURATION: 0.6,
    TEXT_STAGGER: 0.02,
    BG_TRANSITION: 0.5,
    ICON_DURATION: 0.3,
    
    // Animation eases
    CLIP_PATH_EASE: 'power3.inOut',
    TEXT_EASE: 'power2.out',
    BG_EASE: 'power3.inOut',
    ICON_EASE: 'back.inOut(1.7)',
    
    // Rotation amounts
    INITIAL_ROTATION: -5,
    BG_SCALE_HOVER: 1.15,
    BG_SCALE_DEFAULT: 1.1,
    
    // Selectors
    SELECTORS: {
      drawerContainer: '.custom-gsap-drawer',
      detailsElement: '#Details-menu-drawer-container',
      summaryButton: '.header__icon--menu',
      menuDrawer: '#menu-drawer',
      bgImages: '.menu-drawer__bg-img',
      menuItems: '[data-menu-hover]',
      textAnimElements: '[data-text-anim]',
      hamburgerIcon: '.header__icon--menu .icon:not(.icon-close)',
      closeIcon: '.header__icon--menu .icon-close'
    }
  };

  // State management
  let state = {
    isAnimating: false,
    isOpen: false,
    drawerTimeline: null,
    splitTextInstances: [],
    currentBackground: 'default'
  };

  // DOM elements (will be populated on init)
  let elements = {};

  /**
   * Initialize the custom drawer
   */
  function init() {
    // Wait for DOM to be fully loaded
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
      return;
    }

    // Check if GSAP is available
    if (typeof window.gsap === 'undefined' || typeof window.SplitText === 'undefined') {
      console.warn('GSAP or SplitText not loaded. Custom drawer will use default behavior.');
      return;
    }

    // Get DOM elements
    if (!getDOMElements()) {
      console.warn('Required drawer elements not found. Custom drawer disabled.');
      return;
    }

    // Initialize all functionality
    setupInitialState();
    setupEventListeners();
    setupBackgroundSystem();
    setupTextAnimations();
    setupAccessibility();

    console.log('Custom GSAP Drawer initialized successfully');
  }

  /**
   * Get all required DOM elements
   */
  function getDOMElements() {
    const selectors = CONFIG.SELECTORS;
    
    elements.drawerContainer = document.querySelector(selectors.drawerContainer);
    elements.detailsElement = document.querySelector(selectors.detailsElement);
    elements.summaryButton = document.querySelector(selectors.summaryButton);
    elements.menuDrawer = document.querySelector(selectors.menuDrawer);
    elements.bgImages = document.querySelectorAll(selectors.bgImages);
    elements.menuItems = document.querySelectorAll(selectors.menuItems);
    elements.textAnimElements = document.querySelectorAll(selectors.textAnimElements);
    elements.hamburgerIcon = document.querySelector(selectors.hamburgerIcon);
    elements.closeIcon = document.querySelector(selectors.closeIcon);

    // Check if all critical elements exist
    return elements.drawerContainer && 
           elements.detailsElement && 
           elements.summaryButton && 
           elements.menuDrawer;
  }

  /**
   * Setup initial GSAP states
   */
  function setupInitialState() {
    // Create main timeline
    state.drawerTimeline = window.gsap.timeline({ paused: true });

    // Set initial states with GSAP
    window.gsap.set(elements.menuDrawer, {
      clipPath: 'polygon(0 0, 100% 0, 100% 0, 0 0)',
      rotationX: CONFIG.INITIAL_ROTATION,
      transformOrigin: 'top center'
    });

    // Background images initial state
    window.gsap.set(elements.bgImages, { 
      scale: CONFIG.BG_SCALE_DEFAULT,
      opacity: 0 
    });

    // Show default background
    const defaultBg = document.querySelector('[data-bg-for="default"]');
    if (defaultBg) {
      window.gsap.set(defaultBg, { opacity: 1 });
      defaultBg.classList.add('active');
    }

    // Icons initial state
    if (elements.hamburgerIcon) {
      window.gsap.set(elements.hamburgerIcon, { opacity: 1, rotation: 0, scale: 1 });
    }
    
    if (elements.closeIcon) {
      window.gsap.set(elements.closeIcon, { opacity: 0, rotation: 45, scale: 0.8 });
    }

    // Hide animated text initially
    elements.textAnimElements.forEach(el => {
      window.gsap.set(el, { visibility: 'hidden' });
    });
  }

  /**
   * Setup all event listeners
   */
  function setupEventListeners() {
    // Main toggle click
    if (elements.summaryButton) {
      elements.summaryButton.addEventListener('click', handleToggleClick);
    }

    // Prevent default details/summary behavior during animation
    if (elements.detailsElement) {
      elements.detailsElement.addEventListener('toggle', handleDetailsToggle);
    }

    // Keyboard events
    document.addEventListener('keydown', handleKeydown);

    // Click outside to close
    if (elements.menuDrawer) {
      elements.menuDrawer.addEventListener('click', handleOutsideClick);
    }

    // Window resize
    window.addEventListener('resize', debounce(handleResize, 250));

    // Page visibility change
    document.addEventListener('visibilitychange', handleVisibilityChange);
  }

  /**
   * Handle toggle button click
   */
  function handleToggleClick(e) {
    e.preventDefault();
    e.stopPropagation();
    
    if (state.isAnimating) return;

    if (state.isOpen) {
      closeDrawer();
    } else {
      openDrawer();
    }
  }

  /**
   * Handle details element toggle
   */
  function handleDetailsToggle(e) {
    if (state.isAnimating) {
      e.preventDefault();
      return false;
    }
  }

  /**
   * Handle keyboard events
   */
  function handleKeydown(e) {
    switch (e.key) {
      case 'Escape':
        if (state.isOpen) {
          closeDrawer();
        }
        break;
      case 'Tab':
        if (state.isOpen) {
          trapFocus(e);
        }
        break;
    }
  }

  /**
   * Handle clicks outside drawer content
   */
  function handleOutsideClick(e) {
    if (e.target === elements.menuDrawer && state.isOpen) {
      closeDrawer();
    }
  }

  /**
   * Handle window resize
   */
  function handleResize() {
    // Refresh SplitText instances on resize
    if (state.splitTextInstances.length > 0) {
      state.splitTextInstances.forEach(instance => {
        if (instance && instance.revert) {
          instance.revert();
        }
      });
      state.splitTextInstances = [];
      setupTextAnimations();
    }
  }

  /**
   * Handle page visibility change
   */
  function handleVisibilityChange() {
    // Pause animations when page is hidden for performance
    if (document.hidden && state.drawerTimeline) {
      state.drawerTimeline.pause();
    }
  }

  /**
   * Open drawer with GSAP animation
   */
  function openDrawer() {
    if (state.isAnimating) return;
    
    state.isAnimating = true;
    state.isOpen = true;

    // Set accessibility attributes
    elements.detailsElement.setAttribute('open', '');
    elements.summaryButton.setAttribute('aria-expanded', 'true');
    
    // Prevent body scroll
    document.body.style.overflow = 'hidden';

    // Create opening timeline
    state.drawerTimeline.clear();
    
    state.drawerTimeline
      // Clip-path reveal with rotation
      .to(elements.menuDrawer, {
        clipPath: 'polygon(0 0, 100% 0, 100% 100%, 0 100%)',
        rotationX: 0,
        duration: CONFIG.OPEN_DURATION,
        ease: CONFIG.CLIP_PATH_EASE
      }, 0)
      
      // Background zoom
      .to(elements.bgImages, {
        scale: CONFIG.BG_SCALE_DEFAULT,
        duration: CONFIG.OPEN_DURATION * 1.2,
        ease: CONFIG.BG_EASE
      }, 0)
      
      // Hamburger to X animation
      .to(elements.hamburgerIcon, {
        opacity: 0,
        rotation: 45,
        scale: 0.8,
        duration: CONFIG.ICON_DURATION,
        ease: CONFIG.ICON_EASE
      }, 0.1)
      
      .to(elements.closeIcon, {
        opacity: 1,
        rotation: 0,
        scale: 1,
        duration: CONFIG.ICON_DURATION,
        ease: CONFIG.ICON_EASE
      }, 0.1)
      
      // Text animations
      .call(() => {
        animateTextIn();
      }, null, 0.3)
      
      // Complete callback
      .call(() => {
        state.isAnimating = false;
        // Focus management
        const firstFocusable = elements.menuDrawer.querySelector('a, button');
        if (firstFocusable) {
          firstFocusable.focus();
        }
      });

    state.drawerTimeline.play();
  }

  /**
   * Close drawer with GSAP animation
   */
  function closeDrawer() {
    if (state.isAnimating) return;
    
    state.isAnimating = true;
    state.isOpen = false;

    // Create closing timeline
    const closeTimeline = window.gsap.timeline();
    
    closeTimeline
      // Text out animation
      .call(() => {
        animateTextOut();
      }, null, 0)
      
      // X to hamburger animation
      .to(elements.closeIcon, {
        opacity: 0,
        rotation: -45,
        scale: 0.8,
        duration: CONFIG.ICON_DURATION,
        ease: CONFIG.ICON_EASE
      }, 0.1)
      
      .to(elements.hamburgerIcon, {
        opacity: 1,
        rotation: 0,
        scale: 1,
        duration: CONFIG.ICON_DURATION,
        ease: CONFIG.ICON_EASE
      }, 0.1)
      
      // Background reset
      .to(elements.bgImages, {
        scale: CONFIG.BG_SCALE_DEFAULT,
        duration: CONFIG.CLOSE_DURATION,
        ease: CONFIG.BG_EASE
      }, 0.2)
      
      // Clip-path close with rotation
      .to(elements.menuDrawer, {
        clipPath: 'polygon(0 0, 100% 0, 100% 0, 0 0)',
        rotationX: CONFIG.INITIAL_ROTATION,
        duration: CONFIG.CLOSE_DURATION,
        ease: CONFIG.CLIP_PATH_EASE
      }, 0.3)
      
      // Complete callback
      .call(() => {
        // Reset states
        elements.detailsElement.removeAttribute('open');
        elements.summaryButton.setAttribute('aria-expanded', 'false');
        document.body.style.overflow = '';
        
        // Reset to default background
        resetToDefaultBackground();
        
        state.isAnimating = false;
        
        // Return focus to toggle button
        elements.summaryButton.focus();
      });

    closeTimeline.play();
  }

  /**
   * Setup text animations with SplitText
   */
  function setupTextAnimations() {
    elements.textAnimElements.forEach(element => {
      try {
        // Create SplitText instance
        const split = new window.SplitText(element, {
          type: 'chars',
          charsClass: 'char'
        });
        
        // Store reference
        element._splitText = split;
        state.splitTextInstances.push(split);
        
        // Set initial state
        window.gsap.set(split.chars, { 
          yPercent: 100,
          opacity: 0
        });
        
      } catch (error) {
        console.warn('SplitText error for element:', element, error);
      }
    });
  }

  /**
   * Animate text in
   */
  function animateTextIn() {
    elements.textAnimElements.forEach((element, index) => {
      window.gsap.set(element, { visibility: 'visible' });
      
      const split = element._splitText;
      if (split && split.chars && split.chars.length > 0) {
        window.gsap.to(split.chars, {
          yPercent: 0,
          opacity: 1,
          duration: 0.6,
          ease: CONFIG.TEXT_EASE,
          stagger: CONFIG.TEXT_STAGGER,
          delay: index * 0.1
        });
      }
    });
  }

  /**
   * Animate text out
   */
  function animateTextOut() {
    elements.textAnimElements.forEach(element => {
      const split = element._splitText;
      if (split && split.chars && split.chars.length > 0) {
        window.gsap.to(split.chars, {
          yPercent: -100,
          opacity: 0,
          duration: 0.4,
          ease: 'power2.in',
          stagger: 0.01
        });
      }
    });
  }

  /**
   * Setup background switching system
   */
  function setupBackgroundSystem() {
    elements.menuItems.forEach((item) => {
      const bgTarget = item.getAttribute('data-menu-hover');
      
      if (bgTarget) {
        item.addEventListener('mouseenter', () => {
          switchBackground(bgTarget);
        });

        item.addEventListener('mouseleave', () => {
          resetToDefaultBackground();
        });
      }
    });
  }

  /**
   * Switch to a specific background
   */
  function switchBackground(targetId) {
    if (state.currentBackground === targetId) return;
    
    const targetBg = document.querySelector(`[data-bg-for="${targetId}"]`);
    
    if (targetBg) {
      state.currentBackground = targetId;
      
      // Remove active class from all
      elements.bgImages.forEach(bg => bg.classList.remove('active'));
      
      // Add active class to target
      targetBg.classList.add('active');
      
      // Animate transition
      window.gsap.to(elements.bgImages, {
        opacity: 0,
        duration: CONFIG.BG_TRANSITION * 0.8,
        ease: CONFIG.BG_EASE
      });

      window.gsap.to(targetBg, {
        opacity: 1,
        scale: CONFIG.BG_SCALE_HOVER,
        duration: CONFIG.BG_TRANSITION,
        ease: CONFIG.BG_EASE
      });
    }
  }

  /**
   * Reset to default background
   */
  function resetToDefaultBackground() {
    const defaultBg = document.querySelector('[data-bg-for="default"]');
    
    if (defaultBg && state.currentBackground !== 'default') {
      state.currentBackground = 'default';
      
      // Remove active class from all
      elements.bgImages.forEach(bg => bg.classList.remove('active'));
      
      // Add active class to default
      defaultBg.classList.add('active');
      
      // Animate transition
      window.gsap.to(elements.bgImages, {
        opacity: 0,
        scale: CONFIG.BG_SCALE_DEFAULT,
        duration: CONFIG.BG_TRANSITION * 0.8,
        ease: CONFIG.BG_EASE
      });

      window.gsap.to(defaultBg, {
        opacity: 1,
        duration: CONFIG.BG_TRANSITION,
        ease: CONFIG.BG_EASE
      });
    }
  }

  /**
   * Setup accessibility features
   */
  function setupAccessibility() {
    // Add ARIA labels if missing
    if (elements.summaryButton && !elements.summaryButton.getAttribute('aria-label')) {
      elements.summaryButton.setAttribute('aria-label', 'Toggle navigation menu');
    }

    // Ensure proper focus management
    elements.menuDrawer.setAttribute('tabindex', '-1');
  }

  /**
   * Trap focus within the drawer
   */
  function trapFocus(e) {
    if (!state.isOpen) return;

    const focusableElements = elements.menuDrawer.querySelectorAll(
      'a[href], button:not([disabled]), textarea:not([disabled]), input[type="text"]:not([disabled]), input[type="radio"]:not([disabled]), input[type="checkbox"]:not([disabled]), select:not([disabled])'
    );
    
    if (focusableElements.length === 0) return;

    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];

    if (e.shiftKey && document.activeElement === firstFocusable) {
      lastFocusable.focus();
      e.preventDefault();
    } else if (!e.shiftKey && document.activeElement === lastFocusable) {
      firstFocusable.focus();
      e.preventDefault();
    }
  }

  /**
   * Utility: Debounce function
   */
  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func.apply(this, args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  /**
   * Cleanup function
   */
  function cleanup() {
    // Kill timelines
    if (state.drawerTimeline) {
      state.drawerTimeline.kill();
    }
    
    // Revert SplitText instances
    state.splitTextInstances.forEach(instance => {
      if (instance && instance.revert) {
        instance.revert();
      }
    });
    
    // Reset body overflow
    document.body.style.overflow = '';
    
    // Reset drawer state
    if (elements.detailsElement) {
      elements.detailsElement.removeAttribute('open');
    }
    
    if (elements.summaryButton) {
      elements.summaryButton.setAttribute('aria-expanded', 'false');
    }
  }

  /**
   * Handle page unload
   */
  function handleUnload() {
    cleanup();
  }

  // Setup cleanup on page unload
  window.addEventListener('beforeunload', handleUnload);
  window.addEventListener('pagehide', handleUnload);

  // Initialize the drawer
  init();

  // Expose API for external use if needed
  window.CustomGSAPDrawer = {
    open: openDrawer,
    close: closeDrawer,
    toggle: () => state.isOpen ? closeDrawer() : openDrawer(),
    isOpen: () => state.isOpen,
    cleanup: cleanup
  };

})();